"use client";

import { useEffect, useState, useRef } from "react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, Menu, X, Globe } from "lucide-react";
function HeaderMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(false);
  const languageMenuRef = useRef<HTMLDivElement>(null);
  const formatMenuLabel = (label: string): string => {
    return label;
  };
  const filteredMenuItems = menuItems.filter(item => item.label !== "Traditional Chinese" && item.label !== "Traditional Chinese Duplicate");
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (languageMenuRef.current && !languageMenuRef.current.contains(event.target as Node)) {
        setIsLanguageMenuOpen(false);
      }
    };
    if (isLanguageMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isLanguageMenuOpen]);
  if (loading) {
    return <div className="flex gap-4">
        <div className="h-9 w-20 bg-muted/30 animate-pulse rounded-md" />
        <div className="h-9 w-20 bg-muted/30 animate-pulse rounded-md" />
        <div className="h-9 w-20 bg-muted/30 animate-pulse rounded-md" />
      </div>;
  }
  return <>
      {}
      <NavigationMenu viewport={false} className="hidden md:flex">
        <NavigationMenuList className="gap-1">
          {filteredMenuItems.slice(0, 5).map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
              <NavigationMenuLink asChild>
                <Link to={item.href || "#"} className="relative px-4 py-2 text-sm font-medium text-foreground transition-all duration-200 hover:!bg-transparent focus:!bg-transparent after:absolute after:bottom-0 after:left-0 after:h-[2px] after:w-0 after:bg-primary after:transition-all after:duration-300 hover:after:w-full focus:after:w-full">
                  {formatMenuLabel(item.label)}
                </Link>
              </NavigationMenuLink>
            </NavigationMenuItem>)}
        </NavigationMenuList>
      </NavigationMenu>

      {}
      <div className="hidden md:flex items-center gap-2 ml-4">
        {}
        {!isSearchOpen ? <Button variant="ghost" size="icon" onClick={() => setIsSearchOpen(true)} className="hover:!bg-transparent focus:!bg-transparent" aria-label="Open search">
            <Search className="size-5 text-foreground" />
          </Button> : <div className="flex items-center gap-2 animate-in slide-in-from-right-2 duration-200">
            <Input type="search" placeholder="Search..." autoFocus onBlur={() => setIsSearchOpen(false)} className="w-48 h-9 text-sm" />
          </div>}

        {}
        <div className="relative" ref={languageMenuRef}>
          <Button variant="ghost" size="icon" onClick={() => setIsLanguageMenuOpen(!isLanguageMenuOpen)} className="hover:!bg-transparent focus:!bg-transparent" aria-label="Select language" aria-expanded={isLanguageMenuOpen}>
            <Globe className="size-5 text-foreground" />
          </Button>

          {isLanguageMenuOpen && <div className="absolute top-full right-0 mt-2 w-48 bg-background border border-border rounded-md shadow-medium animate-in slide-in-from-top-2 duration-200 z-50">
              <nav className="py-2">
                <Link to="/" className="block px-4 py-2 text-sm font-medium text-foreground hover:bg-accent transition-colors" onClick={() => setIsLanguageMenuOpen(false)}>
                  English
                </Link>
                <Link to="https://alphavision-comm.com/traditional-chinese-duplicate/" className="block px-4 py-2 text-sm font-medium text-foreground hover:bg-accent transition-colors" onClick={() => setIsLanguageMenuOpen(false)}>
                  Traditional Chinese
                </Link>
              </nav>
            </div>}
        </div>
      </div>

      {}
      <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="md:hidden hover:!bg-transparent focus:!bg-transparent" aria-label="Toggle menu">
        {isMobileMenuOpen ? <X className="size-5" /> : <Menu className="size-5" />}
      </Button>

      {}
      {isMobileMenuOpen && <div className="absolute top-full left-0 right-0 bg-background border-b shadow-medium md:hidden animate-in slide-in-from-top-2 duration-200">
          <nav className="container mx-auto px-4 py-4">
            <ul className="flex flex-col gap-2">
              {filteredMenuItems.map((item, i) => <li key={item.id} data-index={i}>
                  <Link to={item.href || "#"} className="block px-4 py-2 text-sm font-medium text-foreground hover:bg-accent rounded-md transition-colors" onClick={() => setIsMobileMenuOpen(false)}>
                    {formatMenuLabel(item.label)}
                  </Link>
                </li>)}
            </ul>

            {}
            <div className="mt-4 px-4 border-t border-border pt-4 space-y-3">
              <Input type="search" placeholder="Search..." className="w-full h-9 text-sm" />
              
              {}
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground px-2">
                  <Globe className="size-4" />
                  <span>Language</span>
                </div>
                <div className="flex flex-col gap-1">
                  <Link to="/" className="block px-4 py-2 text-sm font-medium text-foreground hover:bg-accent rounded-md transition-colors" onClick={() => setIsMobileMenuOpen(false)}>
                    English
                  </Link>
                  <Link to="https://alphavision-comm.com/traditional-chinese-duplicate/" className="block px-4 py-2 text-sm font-medium text-foreground hover:bg-accent rounded-md transition-colors" onClick={() => setIsMobileMenuOpen(false)}>
                    Traditional Chinese
                  </Link>
                </div>
              </div>
            </div>
          </nav>
        </div>}
    </>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header className={`sticky top-0 z-50 w-full bg-light-background border-b border-border transition-all duration-200 ease-out ${isScrolled ? "h-14 shadow-soft" : "h-20"}`}>
      <div className="container mx-auto px-4 h-full flex items-center justify-between">
        <Link to="/#" className="flex-shrink-0">
          <img src="https://top-krill.10web.cloud/wp-content/uploads/2025/12/AV-LOGO-橫式-scaled.png" alt="Alphavision" className="h-8" />
        </Link>

        <MenuProvider menu_id="17" className="flex items-center">
          <HeaderMenuConsumer />
        </MenuProvider>
      </div>
    </header>;
}